import React, { useState } from "react";
import NavBar from "../components/navbar";
import {
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  TextField,
} from "@mui/material";
import ProductTable from "./components/ProductTable";
import { LoadProductList } from "../../../domain/usecases/load-product-list";
import { LoadPositionByEan } from "../../../domain/usecases/load-position-by-ean";
import { Position } from "../../../domain/models/position-model";
import { PositionTableRow } from "../Positions/PositionsTable";
import { useErrorHandler } from "../hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

type Props = {
  loadProducts: LoadProductList;
  loadPosition: LoadPositionByEan;
};

export default function Products({ loadProducts, loadPosition }: Props) {
  const getCurrentWarehouse = useSelector<
    RootState,
    () => { id: number; name: string }
  >((state) => state.getCurrentWarehouse);
  const [positions, setPositions] = useState<Position[]>([]);
  const [code, setCode] = useState("");
  const [ean, setEan] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useErrorHandler(() => {
    alert("Não foi possível carregar os produtos");
  });

  const handleClick = async () => {
    if (!code && !ean && !description) {
      return alert("Preencha ao menos um campo");
    }
    setIsLoading(true);
    try {
      const products = await loadProducts.load({
        code: ean,
        reference: code,
        description,
      });
      const codes = products.map((product) => product.eanCode as string);
      const warehouse = getCurrentWarehouse();
      const res = await loadPosition.load(codes, warehouse.id);
      res.forEach((position) => {
        position.inventories?.forEach((inventory) => {
          const match = products.find(
            (product) => product.eanCode === inventory.item?.code
          );
          inventory.item!.description = match?.description;
          inventory.item!.packageAmount = match?.packageAmount;
          inventory.item!.reference = match?.reference;
          inventory.item!.unit = match?.unit!;
        });
      });
      setPositions(res);
    } catch (error) {
      console.log(error);
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const data: PositionTableRow[] = [];

  positions.forEach((position) => {
    position.inventories?.forEach((inventory) => {
      const row: Partial<PositionTableRow> = {};
      row.id = inventory.id?.toString();
      row.position = `${position.street}.${position.building}.${position.level}.${position.apartment}`;
      row.description = inventory.item?.description;
      row.reference = inventory.item?.reference;
      row.ean = inventory?.item?.code;
      row.amount = inventory.amount.toString();
      row.batch = inventory?.item?.batch?.trim();
      row.packageAmount = inventory.item?.packageAmount;
      row.unit = inventory.item?.unit;
      data.push(row as PositionTableRow);
    });
  });

  return (
    <>
      <NavBar title="Produtos" />
      <Container
        sx={{
          my: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <TextField
          label="Código Interno"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
          }}
        />
        <TextField
          label="Descrição"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
        <TextField
          label="EAN"
          value={ean}
          onChange={(e) => {
            setEan(e.target.value);
          }}
        />
      </Container>
      <Divider>
        <Button
          variant="contained"
          size="large"
          startIcon={
            isLoading && <CircularProgress color="secondary" size={20} />
          }
          onClick={handleClick}
        >
          Pesquisar
        </Button>
      </Divider>
      <Container sx={{ my: "50px" }}>
        <Paper>{positions.length > 0 && <ProductTable data={data} />}</Paper>
      </Container>
    </>
  );
}
